import { useEffect, useState, useCallback } from "react";
import api from "../api/axios";
import { colors, spacing, borderRadius, typography, shadows, createButtonStyle } from "../styles/theme";
const API_URL = process.env.REACT_APP_API_URL;

type GoogleAdsAccount = {
  customer_id: string;
  display_name: string;
};

type IssuedInvoice = {
  id: number;
  invoice_id: string;
  invoice_number?: string;
  client_name: string;
  amount: number;
  issue_date: string;
  email_sent: boolean;
  email_sent_date?: string;
  created_at: string;
  department_name: "semtop" | "adtarget";  // NOWE
  allegro_nicknames?: string[];
  google_ads_accounts?: GoogleAdsAccount[];
};

// Smart pagination helper - generates page numbers with ellipsis
const generatePageNumbers = (currentPage: number, totalPages: number): (number | string)[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];

  // Always show first page
  pages.push(1);

  // Calculate range around current page
  let start = Math.max(2, currentPage - 2);
  let end = Math.min(totalPages - 1, currentPage + 2);

  // If near the beginning, show more pages on the left
  if (currentPage <= 4) {
    start = 2;
    end = Math.min(6, totalPages - 1);
  }

  // If near the end, show more pages on the right
  if (currentPage >= totalPages - 3) {
    start = Math.max(2, totalPages - 5);
    end = totalPages - 1;
  }

  // Add ellipsis if there's a gap after first page
  if (start > 2) {
    pages.push('...');
  }

  // Add pages around current
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // Add ellipsis if there's a gap before last page
  if (end < totalPages - 1) {
    pages.push('...');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
};

const IssuedInvoices = () => {
  const [invoices, setInvoices] = useState<IssuedInvoice[]>([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const totalPages = Math.ceil(totalInvoices / itemsPerPage);

  // Filters
  const [companyFilter, setCompanyFilter] = useState<'semtop' | 'adtarget' | 'all'>('all');  // NOWE

  // PDF-y tylko na żądanie
  const [pdfUrls, setPdfUrls] = useState<{ [id: number]: string }>({});
  const [pdfLoading, setPdfLoading] = useState<{ [id: number]: boolean }>({});

  const fetchInvoices = useCallback(async () => {
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await api.get("/issued-invoices", {
        params: {
          limit: itemsPerPage,
          offset: offset,
          department_name: companyFilter === 'all' ? undefined : companyFilter  // NOWE
        }
      });
      if (response.data?.invoices) {
        setInvoices(response.data.invoices);
        setTotalInvoices(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      alert("Błąd pobierania faktur z bazy danych");
    }
  }, [currentPage, companyFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [companyFilter]);

  const fetchPdf = async (id: number) => {
    if (pdfUrls[id]) return; // już mamy
    setPdfLoading(l => ({ ...l, [id]: true }));
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("Brak tokena JWT – zaloguj się ponownie.");
      const resp = await fetch(`${API_URL}/invoices/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Błąd pobierania PDF – status " + resp.status);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrls(urls => ({ ...urls, [id]: url }));
    } catch (e: any) {
      alert(e.message || "Błąd pobierania PDF.");
    } finally {
      setPdfLoading(l => ({ ...l, [id]: false }));
    }
  };

  const toggleSelect = (invoiceId: string) => {
    const id = parseInt(invoiceId);
    setSelected(s => {
      const copy = new Set(s);
      if (copy.has(id)) copy.delete(id);
      else copy.add(id);
      return copy;
    });
  };

  const handleSendMail = async (id: number) => {
    try {
      await api.post("/invoices/send-many", { invoice_ids: [id] });
      // Odśwież listę, aby zaktualizować status email_sent
      await fetchInvoices();
      alert("Faktura została wysłana mailem!");
    } catch {
      alert("Błąd wysyłki faktury!");
    }
  };

  const handleSendMany = async () => {
    if (selected.size === 0) return;
    setLoading(true);
    try {
      const ids = Array.from(selected);
      const res = await api.post("/invoices/send-many", { invoice_ids: ids });
      if (res.data?.sent) {
        // Odśwież listę, aby zaktualizować status email_sent
        await fetchInvoices();
        alert("Wysłano zaznaczone faktury!");
      }
    } catch {
      alert("Błąd wysyłki wybranych faktur!");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (invoiceId: string) => {
    if (!window.confirm("Na pewno usunąć tę fakturę?")) return;
    try {
      // Usuń z Fakturowni i z bazy (backend robi oba)
      await api.delete("/invoices/delete", { params: { invoice_id: parseInt(invoiceId) } });

      // Odśwież listę
      await fetchInvoices();

      const id = parseInt(invoiceId);
      setSelected(s => { const c = new Set(s); c.delete(id); return c; });
      setPdfUrls(urls => { const { [id]: _, ...rest } = urls; return rest; });
    } catch {
      alert("Błąd usuwania faktury!");
    }
  };

  const handleDeleteMany = async () => {
    if (selected.size === 0) return;
    if (!window.confirm("Na pewno usunąć zaznaczone faktury?")) return;
    setLoading(true);
    try {
      const ids = Array.from(selected);
      // Usuń z Fakturowni i z bazy (backend robi oba)
      await api.delete("/invoices/delete-many", { data: { invoice_ids: ids } });

      // Odśwież listę
      await fetchInvoices();
      setSelected(new Set());

      const idsToRemove = Array.from(selected);
      setPdfUrls(urls => {
        const copy = { ...urls };
        idsToRemove.forEach(id => delete copy[id]);
        return copy;
      });
    } catch {
      alert("Błąd usuwania zaznaczonych faktur!");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = async () => {
    if (selected.size > 0) {
      // Odznacz wszystkie
      setSelected(new Set());
    } else {
      // Zaznacz WSZYSTKIE faktury (pobierz wszystkie IDs z serwera)
      try {
        const response = await api.get("/issued-invoices", {
          params: {
            limit: 100,  // Backend max limit is 100
            offset: 0,
            department_name: companyFilter === 'all' ? undefined : companyFilter  // NOWE
          }
        });
        if (response.data?.invoices) {
          setSelected(new Set(response.data.invoices.map((i: IssuedInvoice) => parseInt(i.invoice_id))));
        }
      } catch (error) {
        console.error("Error fetching all invoices for selection:", error);
        alert("Błąd zaznaczania wszystkich faktur");
      }
    }
  };

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    window.location.href = "/dashboard";
  };

  // Usunięto early return - filtry muszą być widoczne nawet gdy lista pusta

  return (
    <div style={{
      maxWidth: 1200,
      margin: "0 auto",
      padding: spacing.xxl,
    }}>
      <h2 style={{
        fontSize: typography.fontSize.xxxl,
        fontWeight: typography.fontWeight.bold,
        color: colors.textPrimary,
        marginBottom: spacing.xl,
      }}>
        📄 Wystawione faktury
      </h2>

      {/* Filters */}
      <div style={{
        marginBottom: spacing.lg,
        padding: spacing.lg,
        background: colors.backgroundAlt,
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.border}`,
        display: "flex",
        gap: spacing.md,
        alignItems: "center",
        flexWrap: "wrap",
      }}>
        <label style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
            Firma:
          </span>
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value as any)}
            style={{
              padding: spacing.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.sm,
              fontSize: typography.fontSize.sm,
            }}
          >
            <option value="all">Wszystkie</option>
            <option value="semtop">Semtop</option>
            <option value="adtarget">AdTarget</option>
          </select>
        </label>
      </div>

      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: spacing.md,
        marginBottom: spacing.xl,
        padding: spacing.lg,
        background: colors.backgroundAlt,
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.border}`,
      }}>
        <button
          onClick={handleSelectAll}
          style={{
            ...createButtonStyle('secondary'),
            flex: "0 0 auto",
          }}
        >
          {selected.size === invoices.length ? "Odznacz wszystkie" : "Zaznacz wszystkie"}
        </button>
        <button
          onClick={handleSendMany}
          disabled={selected.size === 0 || loading}
          style={{
            ...createButtonStyle('success', selected.size === 0 || loading),
            flex: "0 0 auto",
          }}
        >
          ✉️ Wyślij zaznaczone mailem ({selected.size})
        </button>
        <button
          onClick={handleDeleteMany}
          disabled={selected.size === 0 || loading}
          style={{
            ...createButtonStyle('danger', selected.size === 0 || loading),
            flex: "0 0 auto",
          }}
        >
          🗑️ Usuń zaznaczone ({selected.size})
        </button>
        <button
          onClick={handleBack}
          style={{
            ...createButtonStyle('secondary'),
            marginLeft: "auto",
            flex: "0 0 auto",
          }}
        >
          ← Powrót
        </button>
      </div>

      {totalPages > 1 && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: spacing.md,
          marginBottom: spacing.xl,
          padding: spacing.lg,
          background: colors.backgroundAlt,
          borderRadius: borderRadius.md,
          border: `1px solid ${colors.border}`,
        }}>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={createButtonStyle('secondary', currentPage === 1)}
          >
            ← Poprzednia
          </button>

          <div style={{
            display: "flex",
            gap: spacing.xs,
            flexWrap: "wrap",
          }}>
            {generatePageNumbers(currentPage, totalPages).map((page, index) => (
              page === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  style={{
                    padding: '8px 12px',
                    color: colors.textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => goToPage(page as number)}
                  style={{
                    ...createButtonStyle(page === currentPage ? 'primary' : 'secondary'),
                    minWidth: 40,
                  }}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={createButtonStyle('secondary', currentPage === totalPages)}
          >
            Następna →
          </button>

          <div style={{
            marginLeft: spacing.lg,
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            Strona {currentPage} z {totalPages} ({invoices.length} faktur)
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
          textAlign: "center",
        }}>
          <div style={{
            fontSize: 64,
            marginBottom: spacing.xl,
            opacity: 0.3,
          }}>
            📭
          </div>
          <div style={{
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textSecondary,
            marginBottom: spacing.md,
          }}>
            Brak wystawionych faktur
          </div>
          <div style={{
            fontSize: typography.fontSize.base,
            color: colors.textSecondary,
            marginBottom: spacing.xxl,
          }}>
            {companyFilter !== 'all'
              ? `Brak faktur dla ${companyFilter === 'semtop' ? 'Semtop' : 'AdTarget'}. Zmień filtr aby zobaczyć inne faktury.`
              : 'Gdy wystawisz faktury, pojawią się tutaj'}
          </div>
          <button
            onClick={handleBack}
            style={createButtonStyle('primary')}
          >
            ← Powrót do dashboardu
          </button>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))",
          gap: spacing.xl,
        }}>
          {invoices.map(inv => (
          <div
            key={inv.id}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.md,
              padding: spacing.xl,
              background: colors.background,
              boxShadow: shadows.sm,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = shadows.md;
              e.currentTarget.style.borderColor = colors.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = shadows.sm;
              e.currentTarget.style.borderColor = colors.border;
            }}
          >
            <div style={{
              display: "flex",
              alignItems: "center",
              marginBottom: spacing.md,
              paddingBottom: spacing.md,
              borderBottom: `1px solid ${colors.border}`,
            }}>
              <input
                type="checkbox"
                checked={selected.has(parseInt(inv.invoice_id))}
                onChange={() => toggleSelect(inv.invoice_id)}
                style={{
                  marginRight: spacing.md,
                  width: 18,
                  height: 18,
                  cursor: "pointer",
                }}
              />
              <div style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
              }}>
                {inv.invoice_number || `#${inv.invoice_id}`}
                <span style={{
                  display: 'inline-block',
                  padding: `${spacing.xs}px ${spacing.sm}px`,
                  background: inv.department_name === 'semtop' ? colors.primaryLight : colors.warningLight,
                  color: inv.department_name === 'semtop' ? colors.primary : colors.warning,
                  borderRadius: borderRadius.sm,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.semibold,
                }}>
                  {inv.department_name === 'semtop' ? 'Semtop' : 'AdTarget'}
                </span>
              </div>
              <button
                onClick={() => handleDelete(inv.invoice_id)}
                style={{
                  ...createButtonStyle('danger'),
                  marginLeft: "auto",
                  padding: `${spacing.xs}px ${spacing.md}px`,
                  fontSize: typography.fontSize.sm,
                }}
              >
                🗑️ Usuń
              </button>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: `${spacing.sm}px ${spacing.md}px`,
              marginBottom: spacing.lg,
            }}>
              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                fontWeight: typography.fontWeight.semibold,
              }}>
                Klient:
              </div>
              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textPrimary,
              }}>
                {inv.client_name}
              </div>

              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                fontWeight: typography.fontWeight.semibold,
              }}>
                Konta:
              </div>
              <div>
                {(!inv.allegro_nicknames || inv.allegro_nicknames.length === 0) &&
                 (!inv.google_ads_accounts || inv.google_ads_accounts.length === 0) ? (
                  <span style={{
                    fontSize: typography.fontSize.sm,
                    color: colors.textSecondary,
                  }}>-</span>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.sm, alignItems: "center" }}>
                    {inv.allegro_nicknames?.map((nickname, idx) => (
                      <div key={`allegro-${idx}`} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.xs,
                        padding: `${spacing.xs}px ${spacing.sm}px`,
                        background: colors.warningLight,
                        borderRadius: borderRadius.sm,
                        fontSize: typography.fontSize.xs,
                      }}>
                        <svg width="14" height="14" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
                          <circle cx="8" cy="8" r="7" fill="#FF8C00"/>
                          <text x="8" y="11.5" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial, sans-serif">A</text>
                        </svg>
                        <span>{nickname}</span>
                      </div>
                    ))}
                    {inv.google_ads_accounts?.map((acc, idx) => (
                      <div key={`google-${idx}`} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.xs,
                        padding: `${spacing.xs}px ${spacing.sm}px`,
                        background: colors.primaryLight,
                        borderRadius: borderRadius.sm,
                        fontSize: typography.fontSize.xs,
                      }}>
                        <svg width="14" height="14" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
                          <circle cx="8" cy="8" r="7" fill="#4285F4"/>
                          <text x="8" y="11.5" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial, sans-serif">G</text>
                        </svg>
                        <span>{acc.display_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                fontWeight: typography.fontWeight.semibold,
              }}>
                Kwota:
              </div>
              <div style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.bold,
                color: colors.success,
              }}>
                {inv.amount.toFixed(2)} zł
              </div>

              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                fontWeight: typography.fontWeight.semibold,
              }}>
                Data:
              </div>
              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textPrimary,
              }}>
                {new Date(inv.issue_date).toLocaleDateString('pl-PL')}
              </div>

              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                fontWeight: typography.fontWeight.semibold,
              }}>
                Email:
              </div>
              <div>
                {inv.email_sent ? (
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: spacing.xs,
                    padding: `${spacing.xs}px ${spacing.sm}px`,
                    background: colors.successLight,
                    color: colors.success,
                    borderRadius: borderRadius.sm,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.semibold,
                  }}>
                    ✅ Wysłano: {inv.email_sent_date ? new Date(inv.email_sent_date).toLocaleDateString('pl-PL') : 'Tak'}
                  </div>
                ) : (
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: spacing.xs,
                    padding: `${spacing.xs}px ${spacing.sm}px`,
                    background: colors.warningLight,
                    color: colors.warning,
                    borderRadius: borderRadius.sm,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.semibold,
                  }}>
                    📧 Nie wysłano
                  </div>
                )}
              </div>
            </div>

            <div style={{
              marginTop: spacing.lg,
              paddingTop: spacing.lg,
              borderTop: `1px solid ${colors.border}`,
            }}>
              {pdfUrls[parseInt(inv.invoice_id)] ? (
                <>
                  <iframe
                    src={pdfUrls[parseInt(inv.invoice_id)]}
                    title={`Faktura PDF ${inv.invoice_id}`}
                    width="100%"
                    height="400px"
                    style={{
                      border: `1px solid ${colors.border}`,
                      background: colors.background,
                      borderRadius: borderRadius.sm,
                      marginBottom: spacing.md,
                    }}
                  />
                  <div style={{ display: "flex", gap: spacing.md, flexWrap: "wrap" }}>
                    <a
                      href={pdfUrls[parseInt(inv.invoice_id)]}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        ...createButtonStyle('secondary'),
                        textDecoration: "none",
                        display: "inline-block",
                      }}
                    >
                      🔗 Otwórz w nowej karcie
                    </a>
                    <button
                      onClick={() => {
                        URL.revokeObjectURL(pdfUrls[parseInt(inv.invoice_id)]);
                        setPdfUrls(u => {
                          const { [parseInt(inv.invoice_id)]: _, ...rest } = u;
                          return rest;
                        });
                      }}
                      style={createButtonStyle('secondary')}
                    >
                      ✖️ Zwolnij podgląd
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => fetchPdf(parseInt(inv.invoice_id))}
                  disabled={pdfLoading[parseInt(inv.invoice_id)]}
                  style={{
                    ...createButtonStyle('primary', pdfLoading[parseInt(inv.invoice_id)]),
                    width: "100%",
                  }}
                >
                  {pdfLoading[parseInt(inv.invoice_id)] ? "⏳ Ładowanie PDF…" : "📄 Pokaż PDF"}
                </button>
              )}
            </div>

            {!inv.email_sent && (
              <div style={{ marginTop: spacing.md }}>
                <button
                  onClick={() => handleSendMail(parseInt(inv.invoice_id))}
                  style={{
                    ...createButtonStyle('success'),
                    width: "100%",
                  }}
                >
                  ✉️ Wyślij tę fakturę mailem
                </button>
              </div>
            )}
          </div>
        ))}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: spacing.md,
          marginTop: spacing.xl,
          padding: spacing.lg,
          background: colors.backgroundAlt,
          borderRadius: borderRadius.md,
          border: `1px solid ${colors.border}`,
        }}>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={createButtonStyle('secondary', currentPage === 1)}
          >
            ← Poprzednia
          </button>

          <div style={{
            display: "flex",
            gap: spacing.xs,
            flexWrap: "wrap",
          }}>
            {generatePageNumbers(currentPage, totalPages).map((page, index) => (
              page === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  style={{
                    padding: '8px 12px',
                    color: colors.textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => goToPage(page as number)}
                  style={{
                    ...createButtonStyle(page === currentPage ? 'primary' : 'secondary'),
                    minWidth: 40,
                  }}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={createButtonStyle('secondary', currentPage === totalPages)}
          >
            Następna →
          </button>

          <div style={{
            marginLeft: spacing.lg,
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            Strona {currentPage} z {totalPages} ({invoices.length} faktur)
          </div>
        </div>
      )}
    </div>
  );
};

export default IssuedInvoices;
